// Everything the VoE tab, the Verifications table and their drawer read.
export const VOE_LIST_ITEM_FIELDS = gql`
  fragment VoEListItemFields on VerificationOfEmployment {
    voeId
    createdAt
    updatedAt
    type
    status
    dotNumber
    driverName
    companyName
    contactName
    email
    phoneNumber
    faxNumber
    platform
    atsProviderName
    latestPortalCapture {
      vendor
      employerCode
      url
    }
    carrierRouting {
      preferredPlatform
      platformOnly
    }
    contactSuppression {
      email
      phoneNumber
      faxNumber
    }
    carrierContactCount
    shouldContactEmployer
    employerClassification
    canceledOutOfWindow
    callingHours {
      isOpen
      opensAt
      windowLabel
      closedMessage
    }
    referredCallingHours {
      isOpen
      opensAt
      windowLabel
      closedMessage
    }
    lastContactDate
    contactAttemptCount
    respondedAt
    responseAutonomy
    nextActionOwner
    nextActionReason
    nextActionAt
    safetyRequestConsentFileUrl
    downloadResponseFileUrl
    hasRequestFile
    hasResponseFile
    requestContent {
      voeRequestContentId
      startDate
      endDate
      positionHeld
      reasonForLeaving
      trailerTypes
      isDriverTerminated
      isDriverSubjectToFMCSRs
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT
    }
    responseContent {
      voeResponseContentId
    }
    document {
      documentId
      fileName
      fileType
      userStatus
    }
    documents {
      documentId
      fileName
      fileType
      voeDocumentType
    }
    driverApplicationConsentDocument {
      documentId
      fileName
      fileType
    }
    applicant {
      applicantId
      firstName
      lastName
      applicantStage
      hiringStage
      availableForWorkDate
      hiredDate
      applicationDate
    }
    auditTrailLogs {
      auditTrailId
      createdAt
      type
      title
      message
      contactDate
      email
      phone
      fax
      referredPhone
      referredExtension
      referredFor
      referredContactName
      portalVendor
      portalEmployerCode
      portalUrl
      portalProvider
      infoCollected
      activityLogId
      initiatedBy {
        employeeId
        firstName
        lastName
      }
    }
    actions {
      status
    }
  }
`

export const GET_VOE_LIST = gql`
  ${VOE_LIST_ITEM_FIELDS}
  query VerificationOfEmployments(
    $filters: VoEFiltersInput
    $pageInfo: PageInfoInput
  ) {
    verificationOfEmployments(filters: $filters, pageInfo: $pageInfo) {
      totalCount
      items {
        ...VoEListItemFields
      }
    }
  }
`

// One VoE in the list shape, for a drawer whose VoE left the filtered list
// (e.g. it was just completed and the table hides Completed).
export const GET_VOE_LIST_ITEM = gql`
  ${VOE_LIST_ITEM_FIELDS}
  query VoEListItem($id: String!) {
    verificationOfEmployment(id: $id) {
      ...VoEListItemFields
    }
  }
`

export const GET_VOE_FILE_DOWNLOAD_URL = gql`
  query VoeFileDownloadUrl($voeId: String!, $fileType: String!) {
    voeFileDownloadUrl(voeId: $voeId, fileType: $fileType)
  }
`

export const GET_VOE_TAB_COUNTS = gql`
  query VoETabCounts(
    $allFilters: VoEFiltersInput
    $hiredFilters: VoEFiltersInput
    $onboardingFilters: VoEFiltersInput
    $qualificationFilters: VoEFiltersInput
    $applicationFilters: VoEFiltersInput
    $leadFilters: VoEFiltersInput
  ) {
    all: verificationOfEmployments(
      filters: $allFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
    hired: verificationOfEmployments(
      filters: $hiredFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
    onboarding: verificationOfEmployments(
      filters: $onboardingFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
    qualification: verificationOfEmployments(
      filters: $qualificationFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
    application: verificationOfEmployments(
      filters: $applicationFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
    lead: verificationOfEmployments(
      filters: $leadFilters
      pageInfo: { offset: 0, limit: 0 }
    ) {
      totalCount
    }
  }
`

export const CREATE_VOE = gql`
  mutation CreateVerificationOfEmployment(
    $input: CreateVerificationOfEmploymentInput!
  ) {
    createVerificationOfEmployment(input: $input) {
      voeId
      uploadUrl
    }
  }
`

export const CONFIRM_VOE_CONSENT_UPLOAD = gql`
  mutation ConfirmVoeConsentUpload($voeId: String!) {
    confirmVoeConsentUpload(voeId: $voeId)
  }
`

export const DELETE_VOE = gql`
  mutation DeleteVerificationOfEmployment($id: String!) {
    deleteVerificationOfEmployment(id: $id) {
      voeId
    }
  }
`

export const SUBMIT_VOE_RESPONSE_CONTENT = gql`
  mutation SubmitResponseContent($id: String!, $input: ResponseContentInput!) {
    submitResponseContent(id: $id, input: $input) {
      voeId
      createdAt
      updatedAt
      type
      status
      driverName
      dotNumber
      companyName
      contactName
      email
      phoneNumber
      faxNumber
      platform
      shouldContactEmployer
      responseContent {
        voeResponseContentId
      }
      auditTrailLogs {
        auditTrailId
        title
        message
      }
    }
  }
`

export const GET_VOE_ADDRESS = gql`
  query VoEAddress($id: String!) {
    verificationOfEmployment(id: $id) {
      voeId
      companyAddress {
        street
        city
        state
      }
      employmentExperience {
        address {
          street
          city
          state
        }
      }
    }
  }
`

// Lightweight applicant lookup for the VoE drawer's Applicant Info section.
// Fetched lazily only while the drawer is open so applicant PII (SSN/DOB) is
// never pulled into the VoE list query.
export const GET_VOE_APPLICANT_INFO = gql`
  query VoEApplicantInfo($id: String!) {
    applicant(id: $id) {
      applicantId
      firstName
      lastName
      application {
        applicationId
        dob
        ssn
        issuingCountry
      }
    }
  }
`

export const GET_VOE_BY_ID = gql`
  query VerificationOfEmployment($id: String!) {
    verificationOfEmployment(id: $id) {
      voeId
      createdAt
      updatedAt
      type
      status
      driverName
      ssn
      dob
      completionDate
      assignee {
        employeeId
        firstName
        lastName
      }
      companyAddress {
        street
        city
        state
      }
      dotNumber
      companyName
      contactName
      email
      phoneNumber
      faxNumber
      platform
      referredPhone
      referredExtension
      referredFor
      referredContactName
      nextActionOwner
      nextActionReason
      nextActionAt
      requestContent {
        voeRequestContentId
        startDate
        endDate
        positionHeld
        reasonForLeaving
        trailerTypes
        isDriverTerminated
        isDriverSubjectToFMCSRs
        isJobDesignatedAsSafetySensitiveFunctionUnderDOT
      }
      responseContent {
        voeResponseContentId
        startDate
        endDate
        positionHeld
        reasonForLeaving
        trailerTypes
        isDriverTerminated
        isDriverSubjectToFMCSRs
        isJobDesignatedAsSafetySensitiveFunctionUnderDOT
        eligibleForRehire
      }
      shouldContactEmployer
      documents {
        documentId
        fileName
        fileType
        voeDocumentType
      }
      applicant {
        applicantId
        firstName
        middleName
        lastName
        application {
          applicationId
        }
      }
      auditTrailLogs {
        auditTrailId
        title
        message
        type
        infoCollected
      }
      accidents {
        accidentId
        createdAt
        updatedAt
        accidentDate
        city
        state
        description
        isPreventable
        isCommercialMotorVehicleInvolved
        involvedFatalities
        involvedInjuries
      }
      driverConsentFileUrl
    }
  }
`

export const GET_VOE_DRIVER_NAME_BY_ID = gql`
  query VoEDriverName($id: String!) {
    verificationOfEmployment(id: $id) {
      driverName
    }
  }
`

export const UPDATE_VOE = gql`
  mutation UpdateVerificationOfEmployment(
    $id: String!
    $input: UpdateVerificationOfEmploymentInput!
  ) {
    updateVerificationOfEmployment(id: $id, input: $input) {
      voeId
      createdAt
      updatedAt
      type
      status
      dotNumber
      companyName
      contactName
      email
      phoneNumber
      faxNumber
      platform
      shouldContactEmployer
      requestContent {
        voeRequestContentId
        startDate
        endDate
        positionHeld
        reasonForLeaving
        trailerTypes
        isDriverTerminated
        isDriverSubjectToFMCSRs
        isJobDesignatedAsSafetySensitiveFunctionUnderDOT
      }
    }
  }
`

export const SEND_INSTRUCTIONS_EMAIL = gql`
  mutation sendInstructionsEmail($emailTo: String!) {
    sendInstructionsEmail(emailTo: $emailTo)
  }
`

export const SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE = gql`
  mutation sendInstructionsEmailForOutgoingVoE(
    $emailTo: String!
    $voeId: String!
  ) {
    sendInstructionsEmailForOutgoingVoE(emailTo: $emailTo, voeId: $voeId)
  }
`

export const SUBMIT_EXTERNAL_COMPANY_RESPONSE = gql`
  mutation SubmitExternalCompanyResponse(
    $id: String!
    $input: ResponseContentInput!
  ) {
    submitExternalCompanyResponse(id: $id, input: $input) {
      voeId
      createdAt
      updatedAt
      type
      status
      driverName
      dotNumber
      companyName
      contactName
      email
      phoneNumber
      faxNumber
      platform
      shouldContactEmployer
      responseContent {
        voeResponseContentId
      }
      auditTrailLogs {
        auditTrailId
        title
        message
      }
    }
  }
`

export const MARK_VOE_AS_COMPLETED = gql`
  mutation markVoEAsCompleted($id: String!, $input: MarkVoEAsCompletedInput) {
    markVoEAsCompleted(id: $id, input: $input)
  }
`

export const DECLINE_VOE_REQUEST = gql`
  mutation declineVoERequest($id: String!) {
    declineVoERequest(id: $id) {
      voeId
      status
      completionDate
      auditTrailLogs {
        auditTrailId
        title
        message
      }
    }
  }
`

export const CREATE_VOE_AUDIT_TRAIL = gql`
  mutation createVoEAuditTrail($voeId: String!, $input: VoEAuditTrailInput!) {
    createVoEAuditTrail(voeId: $voeId, input: $input) {
      auditTrailId
      createdAt
      updatedAt
      type
      title
      message
      employee {
        employeeId
        firstName
        lastName
      }
      contactDate
      email
      phone
      fax
    }
  }
`

export const BULK_UPDATE_VOE_STATUS = gql`
  mutation BulkUpdateVoEStatus(
    $ids: [String!]!
    $status: VoEStatus!
    $completion: VoECompletionInput
  ) {
    bulkUpdateVoEStatus(ids: $ids, status: $status, completion: $completion) {
      succeededCount
      failedCount
      skippedCount
    }
  }
`

export const BULK_SEND_VOE_INSTRUCTIONS_EMAIL = gql`
  mutation BulkSendVoEInstructionsEmail($voeIds: [String!]!) {
    bulkSendVoEInstructionsEmail(voeIds: $voeIds) {
      succeededCount
      failedCount
      skippedCount
    }
  }
`

export const GET_VOE_ACTIONS = gql`
  query VoeActions($voeId: String!) {
    voeActions(voeId: $voeId) {
      voeActionId
      voeId
      type
      status
      scheduledAt
      executedAt
      configStepOrder
      callAttempt
      auditTrailId
      createdAt
      updatedAt
    }
  }
`

export const VOE_OUTREACH_PREVIEW = gql`
  query VoEOutreachPreview($voeId: String!) {
    voeOutreachPreview(voeId: $voeId) {
      state
      blockedReason
      blockedPlatform
      blockedWindowMonths
      overridable
      automated
      started
      steps {
        type
        scheduledAt
        status
      }
      nextRound {
        type
        scheduledAt
        status
      }
      sender {
        fullName
        email
        phone
      }
    }
  }
`

export const START_VOE_OUTREACH = gql`
  mutation StartVoEOutreach($voeId: String!, $email: String!) {
    startVoEOutreach(voeId: $voeId, email: $email)
  }
`

const VOE_CALL_AUTOMATION_PREVIEW_FIELDS = `
  state
  blockedReason
  overrides
  windowMonths
  attempts
  firstCallAt
  callingWindow
  nextCallAt
`

export const VOE_CALL_AUTOMATION_PREVIEW = gql`
  query VoECallAutomationPreview($voeId: String!) {
    voeCallAutomationPreview(voeId: $voeId) {
      ${VOE_CALL_AUTOMATION_PREVIEW_FIELDS}
    }
  }
`

export const START_VOE_CALL_AUTOMATION = gql`
  mutation StartVoECallAutomation($voeId: String!) {
    startVoECallAutomation(voeId: $voeId) {
      ${VOE_CALL_AUTOMATION_PREVIEW_FIELDS}
    }
  }
`

export const CANCEL_VOE_ACTION = gql`
  mutation CancelVoEAction($actionId: String!) {
    cancelVoEAction(actionId: $actionId) {
      voeActionId
      status
    }
  }
`

export const RESCHEDULE_VOE_ACTION = gql`
  mutation RescheduleVoEAction($actionId: String!, $newScheduledAt: DateTime!) {
    rescheduleVoEAction(actionId: $actionId, newScheduledAt: $newScheduledAt) {
      voeActionId
      status
      scheduledAt
    }
  }
`

export const SEND_VOE_OUTREACH_NOW = gql`
  mutation SendVoEOutreachNow($voeId: String!, $type: VoEOutreachType!) {
    sendVoEOutreachNow(voeId: $voeId, type: $type) {
      voeActionId
      type
      status
      scheduledAt
    }
  }
`

export const ADD_VOE_ACTION = gql`
  mutation AddVoEAction($voeId: String!, $input: AddVoEActionInput!) {
    addVoEAction(voeId: $voeId, input: $input) {
      voeActionId
      type
      status
      scheduledAt
      createdAt
    }
  }
`

export const GET_VOE_CONTACT_ATTEMPT_COUNT = gql`
  query VoeContactAttemptCount($id: String!) {
    verificationOfEmployment(id: $id) {
      voeId
      contactAttemptCount
      requestContent {
        endDate
      }
    }
  }
`

export const UPDATE_VOE_REFERRAL = gql`
  mutation UPDATE_VOE_REFERRAL(
    $auditTrailId: String!
    $input: UpdateVoEReferralInput!
  ) {
    updateVoEReferral(auditTrailId: $auditTrailId, input: $input) {
      auditTrailId
      referredPhone
      referredExtension
    }
  }
`

export const FIND_VOE_COMPANY_FOR_SWITCH = gql`
  query FindVoECompanyForSwitch($voeId: String!) {
    findVoECompanyForSwitch(voeId: $voeId) {
      voeId
      driverName
      companyId
      companyName
      employeeId
    }
  }
`

export const GENERATE_VOE_AUDIT_TRAIL_PDF = gql`
  mutation GenerateVoEAuditTrailPdf($voeId: String!) {
    generateVoEAuditTrailPdf(voeId: $voeId) {
      documentId
      fileName
      fileType
      voeDocumentType
    }
  }
`
